import React, { Component, Context, ContextType, FormEvent, ReactNode } from 'react';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

import UserActions from '../actions/UserActions';
import ContextInstance from '../context';
import utils from '../utils';

import type { LegacyContext } from './LegacyContextType';
import { AuthContext } from './Router';

type DefaultUserProfileFormState = {
  showPasswordVerification: boolean;
  showPassword: boolean;
  showExistingPassword: boolean;
};

type DefaultUserProfileFormProps = {
  children?: ReactNode;
  // TODO: Temporarily allow unknown props to be accepted.
  [key: string]: unknown;
};

export class DefaultUserProfileForm extends Component<DefaultUserProfileFormProps, DefaultUserProfileFormState> {
  static contextType: Context<LegacyContext> = AuthContext;
  context: ContextType<typeof AuthContext>;

  state: DefaultUserProfileFormState = {
    showPasswordVerification: false,
    showPassword: false,
    showExistingPassword: false,
  };

  onPasswordChanged = (e) => {
    this.setState({
      showPasswordVerification: e.target.value.length > 0,
    });
  };

  handleMouseDownPassword = (e) => e.preventDefault();

  toggleShowPassword = () => this.setState((prevState) => ({ showPassword: !prevState.showPassword }));

  toggleShowExistingPassword = () =>
    this.setState((prevState) => ({
      showExistingPassword: !prevState.showExistingPassword,
    }));

  render() {
    const { showPassword, showExistingPassword, showPasswordVerification } = this.state;
    const { user } = this.context;

    return (
      <UserProfileForm {...this.props}>
        <Grid mt={4} justifyContent="center" container>
          <Grid
            size={{
              xs: 12,
              md: 8,
              lg: 4,
            }}
          >
            <Stack spacing={2} className="sp-update-profile-form">
              <TextField
                label="First name"
                id="givenName"
                name="givenName"
                type="text"
                placeholder="John"
                defaultValue={user.givenName}
                fullWidth
                required
              />

              <TextField
                label="Last name"
                id="surname"
                name="surname"
                type="text"
                placeholder="Snow"
                defaultValue={user.surname}
                fullWidth
                required
              />

              <TextField
                label="Email"
                id="email"
                name="email"
                type="email"
                placeholder="john.snow@example.com"
                defaultValue={user.email}
                fullWidth
                required
              />

              <TextField
                label="Password"
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="keep-it-secret"
                onChange={this.onPasswordChanged}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={this.toggleShowPassword}
                        onMouseDown={this.handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />

              <div>
                {showPasswordVerification && (
                  <TextField
                    label="Existing password"
                    id="existingPassword"
                    name="existingPassword"
                    type={showExistingPassword ? 'text' : 'password'}
                    placeholder="keep-it-secret"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={this.toggleShowExistingPassword}
                            onMouseDown={this.handleMouseDownPassword}
                            edge="end"
                          >
                            {showExistingPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    required
                  />
                )}
              </div>

              <Box key="update-button">
                <Alert severity="error" data-spif="form.error">
                  <span data-spBind="form.errorMessage" />
                </Alert>

                <Button variant="contained" type="submit">
                  <span data-spif="!form.processing">Update</span>
                  <span data-spif="form.processing">Updating...</span>
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </UserProfileForm>
    );
  }
}

type UserProfileFormProps = {
  children?: ReactNode;
  onSubmit?: (e: FormEvent<HTMLFormElement>, next: (err: Error | null, data: unknown) => void) => void;
  // TODO: Temporarily allow unknown props to be accepted.
  [key: string]: unknown;
};

type UserProfileFormState = {
  fields: Record<string, unknown>;
  defaultFields: unknown;
  errorMessage: string | null;
  isFormProcessing: boolean;
  isFormSuccessful: boolean;
};

// eslint-disable-next-line react/no-multi-comp
export default class UserProfileForm extends Component<UserProfileFormProps, UserProfileFormState> {
  static contextType: Context<LegacyContext> = AuthContext;
  context: ContextType<typeof AuthContext>;

  constructor(props: UserProfileFormProps, context: ContextType<typeof AuthContext>) {
    super(props, context);

    // (CHANGED: React 18) Initialise state after context is available (React sets context before calling constructor)
    this.state = {
      fields: {},
      defaultFields: context?.user ?? {},
      errorMessage: null,
      isFormProcessing: false,
      isFormSuccessful: false,
    } as UserProfileFormState;
  }

  _updateSessionData = (data, callback) => {
    const sessionStore = ContextInstance.sessionStore;

    if (!sessionStore.empty()) {
      let hasChanged = false;
      const { account } = utils.clone(sessionStore.get());

      for (const key in data) {
        if (key in account) {
          if (account[key] != data[key]) {
            hasChanged = true;
            account[key] = data[key];
          }
        }
      }

      if (hasChanged) {
        ContextInstance.userStore.resolveSession(callback, true);
      } else {
        return callback();
      }
    }
  };

  _onFormSubmit(e) {
    e.preventDefault();
    e.persist();

    const next = (err, data) => {
      if (err) {
        return this.setState({
          isFormProcessing: false,
          isFormSuccessful: false,
          errorMessage: err.message,
        });
      }

      // If the user didn't specify any data,
      // then simply default to what we have in state.
      data = data || this.state.fields;

      UserActions.updateProfile(data, (err) => {
        if (err) {
          return this.setState({
            isFormProcessing: false,
            isFormSuccessful: false,
            errorMessage: err.message,
          });
        }

        this._updateSessionData(data, () => {
          this.setState({
            isFormProcessing: false,
            isFormSuccessful: true,
            errorMessage: null,
          });
        });
      });
    };

    this.setState({
      isFormProcessing: true,
    });

    const formData = new FormData(e.target);
    const fields = {
      givenName: formData.get('givenName'),
      surname: formData.get('surname'),
      email: formData.get('email'),
    };

    const password = formData.get('password');
    if (password) {
      fields['password'] = password;
      fields['existingPassword'] = formData.get('existingPassword');
    }

    if (this.props.onSubmit) {
      e.data = fields;
      this.props.onSubmit(e, next);
    } else {
      return next(null, fields);
    }
  }

  _mapFormFieldHandler(element, tryMapField) {
    const defaultValue = element.props.name ? utils.getFieldValue(this.state.defaultFields, element.props.name) : null;

    utils.mapFormField(element, tryMapField, defaultValue);
  }

  _spIfHandler(action) {
    let test = null;

    switch (action) {
      case 'form.successful':
        test = this.state.isFormSuccessful;
        break;
      case 'form.processing':
        test = this.state.isFormProcessing;
        break;
      case 'form.error':
        test = !!this.state.errorMessage;
        break;
    }

    return test;
  }

  _spBindHandler(bind, element) {
    const result = false;

    switch (bind) {
      case 'form.errorMessage': {
        const className = element.props ? element.props.className : null;
        return <span className={className}>{this.state.errorMessage}</span>;
      }
    }

    return result;
  }

  render() {
    if (this.props.children) {
      const selectedProps = utils.excludeProps(['onSubmit', 'children'], this.props);

      return (
        <form onSubmit={this._onFormSubmit.bind(this)} {...selectedProps}>
          {utils.makeForm(
            this,
            this._mapFormFieldHandler.bind(this),
            this._spIfHandler.bind(this),
            this._spBindHandler.bind(this),
          )}
        </form>
      );
    } else {
      return <DefaultUserProfileForm {...this.props} />;
    }
  }
}
